/**
 * useAudio - React hook for audio feedback integration
 * 
 * Provides easy access to audio functionality throughout the app
 */

import { useEffect, useCallback, useState, useRef } from 'react';
// Lazy import audioManager to avoid initialization order issues (audioManager -> logger)
// import audioManager from '../utils/audioManager';
import { logger } from '../utils/logger';

export const useAudio = () => {
  // Cache lazy-loaded audioManager
  const audioManagerRef = useRef(null);
  const getAudioManager = async () => {
    if (!audioManagerRef.current) {
      audioManagerRef.current = (await import('../utils/audioManager')).default;
    }
    return audioManagerRef.current;
  };
  
  const [audioStatus, setAudioStatus] = useState({ isInitialized: false, isMuted: false, volume: 0.7 });
  const [isInitialized, setIsInitialized] = useState(false);

  // Update audio status (lazy load audioManager)
  useEffect(() => {
    let interval;
    const updateStatus = async () => {
      const audioManager = await getAudioManager();
      const status = audioManager.getStatus();
      setAudioStatus(status);
      setIsInitialized(status.isInitialized);
    };

    updateStatus();
    
    // Check status periodically
    interval = setInterval(updateStatus, 2000);
    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  // Initialize audio on first user interaction
  const initializeAudio = useCallback(async () => {
    if (!isInitialized) {
      const audioManager = await getAudioManager();
      const success = await audioManager.initialize();
      setIsInitialized(success);
      return success;
    }
    return true;
  }, [isInitialized]);

  // Play sound with automatic initialization
  const playSound = useCallback(async (soundType, options = {}) => {
    try {
      // Check user's sound_enabled preference from userProfile in localStorage
      try {
        const storedProfile = localStorage.getItem('userProfile');
        if (storedProfile) {
          const userProfile = JSON.parse(storedProfile);
          const soundEnabled = userProfile?.preferences?.sound_enabled !== false; // Default to true if not set
          if (!soundEnabled) {
            logger.debug('Sound disabled by user preference');
            return; // Don't play sound if disabled
          }
        }
      } catch (e) {
        // If we can't read preferences, default to enabled
        logger.debug('Could not read userProfile, defaulting to sound enabled');
      }
      
      // Auto-initialize if needed
      if (!isInitialized) {
        await initializeAudio();
      }
      
      const audioManager = await getAudioManager();
      await audioManager.playSound(soundType, options);
    } catch (error) {
      logger.warn(`Failed to play sound ${soundType}:`, error);
    }
  }, [isInitialized, initializeAudio]);

  // Convenience methods for common sounds
  const playCorrect = useCallback((options) => playSound('correct', options), [playSound]);
  const playIncorrect = useCallback((options) => playSound('incorrect', options), [playSound]);
  const playComplete = useCallback((options) => playSound('complete', options), [playSound]);
  const playClick = useCallback((options) => playSound('click', options), [playSound]);
  const playAchievement = useCallback((options) => playSound('achievement', options), [playSound]);
  const playCelebration = useCallback((options) => playSound('celebration', options), [playSound]);

  // Haptic feedback
  const triggerHaptic = useCallback(async (type = 'light') => {
    const audioManager = await getAudioManager();
    audioManager.triggerHaptic(type);
  }, []);

  // Volume and mute controls
  const setVolume = useCallback(async (volume) => {
    const audioManager = await getAudioManager();
    audioManager.setVolume(volume);
  }, []);

  const setMuted = useCallback(async (muted) => {
    const audioManager = await getAudioManager();
    audioManager.setMuted(muted);
  }, []);

  const setHapticEnabled = useCallback(async (enabled) => {
    const audioManager = await getAudioManager();
    audioManager.setHapticEnabled(enabled);
  }, []);

  return {
    // Status
    audioStatus,
    isInitialized,
    isSupported: audioStatus.isSupported,
    isMuted: audioStatus.isMuted,
    volume: audioStatus.volume,
    hapticEnabled: audioStatus.hapticEnabled,
    hasHapticSupport: audioStatus.hasHapticSupport,

    // Methods
    initializeAudio,
    playSound,
    
    // Convenience methods
    playCorrect,
    playIncorrect,
    playComplete,
    playClick,
    playAchievement,
    playCelebration,
    
    // Haptic feedback
    triggerHaptic,
    
    // Settings
    setVolume,
    setMuted,
    setHapticEnabled,
    
    // Direct access to manager (lazy-loaded)
    getAudioManager
  };
};

export default useAudio;